import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { authAPI } from '../../services/api';
import templeImg from '../../assets/temple-far1.jpg';
import './ProfilePage.css';

function PasswordInput({ show, onToggle, ...props }) {
  return (
    <div className="pf-pw">
      <input className="pf-in" type={show ? 'text' : 'password'} {...props} />
      <button type="button" className="pf-eye" onClick={onToggle} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  );
}

export default function ProfilePage() {
  const { user, updateUser } = useAuth();

  const [tab,    setTab]    = useState('profile');     // profile | password | delete
  const [name,   setName]   = useState(user?.name || '');
  const [pw,     setPw]     = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [del,    setDel]    = useState({ text: '', password: '' });
  const [busy,   setBusy]   = useState('');            // save | pw | del
  const [note,   setNote]   = useState(null);          // { type: 'ok' | 'err', text }

  const switchTab = t => { setTab(t); setNote(null); };

  const handleSave = async e => {
    e.preventDefault();
    setBusy('save'); setNote(null);
    try {
      // Target year and subjects are no longer edited here; they are passed through untouched.
      const res = await authAPI.updateProfile({
        name,
        targetYear: user?.targetYear || 2026,
        preferredSubjects: user?.preferredSubjects || [],
      });
      updateUser(res.data.user);
      setNote({ type: 'ok', text: 'Saved.' });
    } catch (error) {
      setNote({ type: 'err', text: error.response?.data?.message || 'Update failed.' });
    } finally { setBusy(''); }
  };

  const handlePassword = async e => {
    e.preventDefault();
    if (pw.next !== pw.confirm) { setNote({ type: 'err', text: 'Passwords do not match.' }); return; }
    if (pw.next.length < 8) { setNote({ type: 'err', text: 'Use at least 8 characters.' }); return; }
    setBusy('pw'); setNote(null);
    try {
      await authAPI.changePassword({ currentPassword: pw.current, newPassword: pw.next });
      setNote({ type: 'ok', text: 'Password changed.' });
      setPw({ current: '', next: '', confirm: '' });
    } catch (error) {
      setNote({ type: 'err', text: error.response?.data?.message || 'Change failed.' });
    } finally { setBusy(''); }
  };

  const handleDelete = async e => {
    e.preventDefault();
    if (del.text !== 'DELETE') { setNote({ type: 'err', text: 'Type DELETE exactly.' }); return; }
    if (!del.password) { setNote({ type: 'err', text: 'Enter your password.' }); return; }
    setBusy('del'); setNote(null);
    try {
      await authAPI.deleteAccount({ password: del.password });
      // Clear everything locally, then leave
      localStorage.clear();
      window.location.href = '/';
    } catch (error) {
      setNote({ type: 'err', text: error.response?.data?.message || 'Deletion failed. Check your password.' });
      setBusy('');
    }
  };

  const toggle = () => setShowPw(s => !s);

  return (
    <div className="pf">
      <h1 className="pf-sr">Profile</h1>

      {/* Part one: who you are, over the temple */}
      <aside className="pf-art" style={{ backgroundImage: `url(${templeImg})` }}>
        <div className="pf-id">
          <span className="pf-avatar">{user?.name?.[0]?.toUpperCase() || '?'}</span>
          <div className="pf-who">
            <b>{user?.name}</b>
            <span>{user?.email}</span>
          </div>
          <div className="pf-stats">
            <span><b>{user?.studyStreak || 0}</b> streak</span>
            <span><b>{user?.totalCardsStudied || 0}</b> cards</span>
          </div>
        </div>
      </aside>

      {/* Part two: the forms, one at a time */}
      <section className="pf-main">
        <div className="pf-box">
          <div className="pf-tabs" role="tablist" aria-label="Profile">
            <button type="button" role="tab" aria-selected={tab === 'profile'} className={tab === 'profile' ? 'on' : ''} onClick={() => switchTab('profile')}>Profile</button>
            <button type="button" role="tab" aria-selected={tab === 'password'} className={tab === 'password' ? 'on' : ''} onClick={() => switchTab('password')}>Password</button>
            <button type="button" role="tab" aria-selected={tab === 'delete'} className={`danger ${tab === 'delete' ? 'on' : ''}`} onClick={() => switchTab('delete')}>Delete</button>
          </div>

          {note && <p className={`pf-note ${note.type}`} role="alert">{note.text}</p>}

          {tab === 'profile' && (
            <form className="pf-form" onSubmit={handleSave}>
              <label className="pf-field"><span>Name</span>
                <input className="pf-in" value={name} onChange={e => setName(e.target.value)} autoComplete="name" required />
              </label>
              <label className="pf-field"><span>Email</span>
                <input className="pf-in" value={user?.email || ''} disabled readOnly />
              </label>
              <button className="pf-btn" type="submit" disabled={busy === 'save'}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            </form>
          )}

          {tab === 'password' && (
            <form className="pf-form" onSubmit={handlePassword}>
              <label className="pf-field"><span>Current password</span>
                <PasswordInput show={showPw} onToggle={toggle} autoComplete="current-password" required
                  value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} />
              </label>
              <label className="pf-field"><span>New password</span>
                <PasswordInput show={showPw} onToggle={toggle} autoComplete="new-password" required
                  value={pw.next} onChange={e => setPw(p => ({ ...p, next: e.target.value }))} />
              </label>
              <label className="pf-field"><span>Confirm password</span>
                <PasswordInput show={showPw} onToggle={toggle} autoComplete="new-password" required
                  value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} />
              </label>
              <button className="pf-btn" type="submit" disabled={busy === 'pw'}>{busy === 'pw' ? 'Changing…' : 'Change password'}</button>
            </form>
          )}

          {tab === 'delete' && (
            <form className="pf-form" onSubmit={handleDelete}>
              <p className="pf-warn">Permanently deletes your account and all progress. No recovery.</p>
              <label className="pf-field"><span>Type DELETE</span>
                <input className="pf-in" value={del.text} placeholder="DELETE" autoComplete="off" spellCheck={false}
                  onChange={e => setDel(d => ({ ...d, text: e.target.value }))} />
              </label>
              <label className="pf-field"><span>Password</span>
                <PasswordInput show={showPw} onToggle={toggle} autoComplete="current-password"
                  value={del.password} onChange={e => setDel(d => ({ ...d, password: e.target.value }))} />
              </label>
              <button className="pf-btn pf-btn--rust" type="submit" disabled={busy === 'del' || del.text !== 'DELETE' || !del.password}>
                {busy === 'del' ? 'Deleting…' : 'Delete forever'}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}